import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormikProps } from 'formik';
import {
    LegalProceeding,
    LegalProceedingAnnexes,
    LegalProceedingProcessData,
    LegalProceedingProduct,
} from '../../../../../shared/types/legalProceedings/legalProceedings';
import {
    asyncCreateLegalProceeding,
    asyncGetLegalProceeding,
    asyncUpdateLegalProceeding,
} from '../../../../../services/legalProceeding.service';
import { Attachment, ISelect } from '../../../../../shared/types/shared';
import { cleanObj } from '../../../../../shared/utils/objects';
import { ToastSwal } from '../../../../components/general/toastSwals/toastSwal';
import {
    LEGAL_PROCEEDINGS,
    LEGAL_PROCEEDINGS_DETAIL,
} from '../../../../../routes/paths';

export const useLegalProceedingsForm = () => {
    const navigate = useNavigate();
    const { idLegalProceeding } = useParams();
    const [legalProceeding, setLegalProceeding] = useState<LegalProceeding>();
    const [loading, setLoading] = useState(false);
    const [
        formikLegalProceedingProcessData,
        setFormikLegalProceedingProcessData,
    ] = useState<FormikProps<LegalProceedingProcessData>>();
    const [formikLegalProceedingProduct, setFormikLegalProceedingProduct] =
        useState<FormikProps<LegalProceedingProduct>>();
    const [formikLegalProceedingAnnexes, setFormikLegalProceedingAnnexes] =
        useState<FormikProps<LegalProceedingAnnexes>>();
    const [attachments, setAttachments] = useState<Attachment[]>([]);

    const isValidForm =
        formikLegalProceedingProcessData?.isValid &&
        formikLegalProceedingProduct &&
        formikLegalProceedingAnnexes;

    const handleSubmit = async () => {
        await formikLegalProceedingProcessData?.submitForm();
        await formikLegalProceedingProduct?.submitForm();
        await formikLegalProceedingAnnexes?.submitForm();

        if (!isValidForm) return;

        setLoading(true);

        const data: LegalProceeding = {};
        data.processData = cleanObj(formikLegalProceedingProcessData?.values);
        data.product = cleanObj(formikLegalProceedingProduct?.values);
        data.annexes = cleanObj(formikLegalProceedingAnnexes?.values);
        data.attachments = attachments;

        let res: LegalProceeding | undefined = undefined;

        try {
            if (legalProceeding && legalProceeding.id)
                res = await asyncUpdateLegalProceeding(
                    legalProceeding.id,
                    data
                );
            else res = await asyncCreateLegalProceeding(data);
        } catch (error) {}

        if (res) {
            ToastSwal.fire({
                text: `¡Felicidades! El proceso judicial se ${
                    legalProceeding && legalProceeding.id ? 'actualizo' : 'creó'
                } satisfactoriamente.`,
                icon: 'success',
                timer: 5000,
                position: 'center-end',
            });
            navigate(`${LEGAL_PROCEEDINGS_DETAIL}/${res.id}`);
        } else {
            ToastSwal.fire({
                text: `Lo sentimos, no se pudo ${
                    legalProceeding && legalProceeding.id
                        ? 'actualizar'
                        : 'crear'
                } el proceso judicial. Por favor, asegúrate de ${
                    legalProceeding && legalProceeding.id
                        ? 'diligenciar los datos correctamente'
                        : 'que no exista previamente'
                } e intenta nuevamente más tarde.`,
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
        }

        setLoading(false);
    };

    const handleCleanAndBack = () => {
        formikLegalProceedingProcessData?.resetForm();
        formikLegalProceedingProduct?.resetForm();
        formikLegalProceedingAnnexes?.resetForm();
        navigate(LEGAL_PROCEEDINGS);
    };
    useEffect(() => {
        if (Boolean(idLegalProceeding?.trim())) {
            setLoading(true);
            asyncGetLegalProceeding(idLegalProceeding?.trim())
                .then((legalProceeding) => {
                    if (!legalProceeding || !legalProceeding.id)
                        navigate(LEGAL_PROCEEDINGS);

                    [
                        legalProceeding?.processData,
                        legalProceeding?.product,
                        legalProceeding?.annexes,
                    ].forEach((item: any) => {
                        if (item)
                            Object.keys(item).forEach((key) => {
                                if (item?.[key] && (item?.[key] as ISelect)?.id)
                                    item[key] = (
                                        item[key] as ISelect
                                    )?.id.toString();
                            });
                    });

                    setLegalProceeding(legalProceeding);
                })
                .finally(() => setLoading(false));
        }
    }, [idLegalProceeding]);

    return {
        navigate,
        legalProceeding,
        loading,
        isValidForm,
        handleSubmit,
        handleCleanAndBack,
        formikLegalProceedingProcessData,
        formikLegalProceedingProduct,
        formikLegalProceedingAnnexes,
        attachments,
        setFormikLegalProceedingProcessData,
        setFormikLegalProceedingProduct,
        setFormikLegalProceedingAnnexes,
        setAttachments,
    };
};
